import React, { useEffect, useRef, useState } from "react";
import MicWave from './MicWave';
import { init, handleEvent } from '../controllers/ChatController';
import ReactMarkdown from 'react-markdown';
import PageShell, { Btn, Pill } from '../components/design/PageShell';

const RealtimeChat = () => {
  const [loading, setLoading] = useState(false);
  const [responseTexts, setResponseTexts] = useState([]);
  const [chatActive, setChatActive] = useState(false);
  const audioRef = useRef(null);
  const dataChannelRef = useRef(null);
  const peerConnectionRef = useRef(null);
  const messagesEndRef = useRef(null);
  const [userMessage, setUserMessage] = useState("");

  const startConversation = () => {
    if (chatActive) {
      if (peerConnectionRef.current) {
        peerConnectionRef.current.close();
        peerConnectionRef.current = null;
      }
      setChatActive(false);
    } else {
      init(setLoading, peerConnectionRef, dataChannelRef, setResponseTexts);
      setChatActive(true);
    }
  };

  useEffect(() => {
    return () => {
      if (peerConnectionRef.current) peerConnectionRef.current.close();
    };
  }, []);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [responseTexts]);

  const handleSendMessage = () => {
    if (userMessage.trim() !== "") {
      setResponseTexts(prev => [...prev, { message: userMessage, type: "user", isTranscribing: false }]);
      handleEvent({ type: "user_message", content: userMessage }, setResponseTexts, dataChannelRef);
      setUserMessage("");
    }
  };

  return (
    <PageShell
      section="chatgpt"
      number="11"
      title="Assistant"
      subtitle="Conversation avec gpt-4o · contexte comptabilité + activité"
      actions={
        <>
          <Btn kind={chatActive ? 'accent' : 'ghost'} onClick={startConversation}>
            {chatActive ? '● Connecté' : 'Activer'} voice
          </Btn>
        </>
      }
    >
      <div style={{ display: 'grid', gridTemplateColumns: '280px 1fr', gap: 0, border: '1px solid var(--rule)', minHeight: 580 }} className="ta-chat-layout">
        <aside style={{ borderRight: '1px solid var(--rule)', padding: '20px 0' }}>
          <div className="eyebrow" style={{ padding: '0 16px 12px' }}>// État</div>
          <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--rule)' }}>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              <Pill tone={chatActive ? 'ok' : 'muted'}>{chatActive ? 'actif' : 'inactif'}</Pill>
            </div>
            <div className="mono" style={{ fontSize: 10, color: 'var(--ink-4)', marginTop: 10, letterSpacing: '0.08em' }}>
              {chatActive ? 'connexion RTC ouverte' : 'micro désactivé'}
            </div>
          </div>
          <div className="eyebrow" style={{ padding: '16px 16px 12px' }}>// Messages</div>
          <div style={{ padding: '14px 16px' }}>
            <div className="mono" style={{ fontSize: 10, color: 'var(--ink-3)', letterSpacing: '0.08em' }}>
              {responseTexts.length} messages · GPT-4o Realtime
            </div>
          </div>
        </aside>
        <section style={{ display: 'flex', flexDirection: 'column' }}>
          <div style={{ padding: 24, borderBottom: '1px solid var(--rule)' }}>
            <div style={{ fontSize: 20, letterSpacing: '-0.015em' }}>Jarvis · Assistant vocal</div>
            <div className="mono" style={{ fontSize: 10, color: 'var(--ink-3)', letterSpacing: '0.14em', marginTop: 6 }}>
              API temps réel · voix française
            </div>
          </div>
          <div style={{ flex: 1, padding: 24, display: 'flex', flexDirection: 'column', gap: 24, minHeight: 380, maxHeight: 540, overflowY: 'auto' }}>
            {loading && <div className="mono" style={{ color: 'var(--ink-3)' }}>Connexion au modèle…</div>}
            {responseTexts.map((t, i) => {
              const user = t.type === 'user';
              return (
                <div key={i} style={{ display: 'grid', gridTemplateColumns: '36px 1fr', gap: 16 }}>
                  <div className="mono" style={{
                    width: 28, height: 28, display: 'grid', placeItems: 'center',
                    fontSize: 10, letterSpacing: '0.1em',
                    border: `1px solid ${user ? 'var(--rule-2)' : 'var(--accent)'}`,
                    color: user ? 'var(--ink-3)' : 'var(--accent)',
                  }}>
                    {user ? 'MS' : 'AI'}
                  </div>
                  <div style={{ fontSize: 13.5, lineHeight: 1.65, color: user ? 'var(--ink-2)' : 'var(--ink)', maxWidth: 620 }}>
                    {typeof t.message === 'string' ? <ReactMarkdown>{t.message}</ReactMarkdown> : t.message}
                  </div>
                </div>
              );
            })}
            <div ref={messagesEndRef} />
          </div>
          <div style={{ padding: '16px 24px', borderTop: '1px solid var(--rule)', display: 'flex', alignItems: 'center', gap: 16 }}>
            <input
              placeholder="Demande quelque chose à ton assistant…"
              value={userMessage}
              onChange={(e) => setUserMessage(e.target.value)}
              onKeyPress={(e) => { if (e.key === 'Enter') handleSendMessage(); }}
              style={{ flex: 1, background: 'transparent', border: 0, padding: '8px 0', fontSize: 14, color: 'var(--ink)', outline: 'none', fontFamily: 'var(--font-sans)' }}
            />
            <button
              onClick={handleSendMessage}
              className="mono"
              style={{ color: 'var(--accent)', fontSize: 11, letterSpacing: '0.14em', background: 'transparent', border: 0, cursor: 'pointer', textTransform: 'uppercase' }}
            >
              envoyer ↵
            </button>
          </div>
        </section>
      </div>

      <div style={{ marginTop: 24, display: 'flex', justifyContent: 'flex-end' }}>
        <div onClick={startConversation} style={{ cursor: 'pointer' }}>
          <MicWave animated={chatActive} />
        </div>
      </div>

      <audio ref={audioRef} autoPlay />

      <style>{`
        @media (max-width: 768px) {
          .ta-chat-layout { grid-template-columns: 1fr !important; }
          .ta-chat-layout aside { border-right: 0 !important; border-bottom: 1px solid var(--rule); }
        }
      `}</style>
    </PageShell>
  );
};

export default RealtimeChat;
